import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/ui/DataTable';
import { canAccessProviderAdmin } from './providerAdminAccess';

const ADMISSION_PROFILE_LABELS = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  ACTIVE: 'Active',
};

function admissionProfileLabel(status) {
  if (status == null || status === '') return 'None';
  const key = String(status).toUpperCase();
  return ADMISSION_PROFILE_LABELS[key] || String(status);
}

function isForbidden(error) {
  return error?.status === 403;
}

export function ProgramsPage() {
  const { session } = useAuth();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const allowed = canAccessProviderAdmin(session);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await providerAdminApi.listProviderPrograms({ page: 1, pageSize: 100 });
      setForbidden(false);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setItems([]);
      if (isForbidden(err)) {
        setForbidden(true);
        setError(null);
      } else {
        setForbidden(false);
        setError(err.message || 'Failed to load programs');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) {
      load();
    } else {
      setLoading(false);
    }
  }, [allowed, load]);

  if (!allowed || forbidden) {
    return (
      <p className="text-sm text-eorig-muted">
        Provider Admin access is required to view programs.
      </p>
    );
  }

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'code', header: 'Code' },
    { key: 'organization_name', header: 'Originator' },
    {
      key: 'custodian_organization_name',
      header: 'Custodian',
      render: (row) => row.custodian_organization_name || '—',
    },
    {
      key: 'is_active',
      header: 'Active',
      render: (row) => (row.is_active ? 'Yes' : 'No'),
    },
    {
      key: 'admission_profile_status',
      header: 'Admission profile',
      render: (row) => admissionProfileLabel(row.admission_profile_status),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-eorig-muted">
          Programs belong to an originator in this provider&apos;s scope.
        </p>
        <Link
          to="/provider/admin/programs/new"
          className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white"
        >
          Add program
        </Link>
      </div>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? (
        <p className="text-sm text-eorig-muted">Loading programs…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted">
          No programs for originators in this provider&apos;s scope.
        </div>
      ) : (
        <DataTable columns={columns} rows={items} rowKey={(row) => row.id} />
      )}
    </div>
  );
}
